import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HeaderConfigurationComponent } from '../../../../../../../components/ecosystems/header-configuration/header-configuration.component';
import { InformationTitleComponent } from '../../../../../../../components/ecosystems/information-title/information-title.component';
import {
  IonButton,
  IonContent,
  IonInput,
  ModalController,
} from '@ionic/angular/standalone';
import { LabelDescriptionComponent } from '../../../../../../../components/ecosystems/label-description/label-description.component';
import { LargedLinkComponent } from '../../../../../../../components/ecosystems/larged-link/larged-link.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
  DeviceActionStackService,
  EdroNetworkManager,
} from '../../../../../manager/edro-network-manager';
import { Pool, PoolAdditionalData } from '../../../../../../../apis/pool.api';
import { IProduct } from '../../../../IProduct';
import { SlideModalController } from '../../../../../../slide-modal.service';
import { P0003605EnSavoirPlusDosageComponent } from '../../../P0003605/InjectionChlore/EnSavoirPlus/p0003605-en-savoir-plus-dosage/p0003605-en-savoir-plus-dosage.component';
import { TwoChoicesModalComponent } from '../../../../../../../components/ecosystems/two-choices-modal/two-choices-modal.component';
import { FunctionEnSavoirPlusModalComponent } from '../../../../../../../components/ecosystems/function-en-savoir-plus-modal/function-en-savoir-plus-modal.component';
import { P0003606PositionTypeEnum } from '../../Enum/P0003605PositionType';

@Component({
  selector: 'app-p0003606-installation-dosage',
  templateUrl: './p0003606-installation-dosage.component.html',
  styleUrls: ['./p0003606-installation-dosage.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    HeaderConfigurationComponent,
    InformationTitleComponent,
    IonButton,
    IonContent,
    IonInput,
    LabelDescriptionComponent,
    LargedLinkComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class P0003606InstallationDosageComponent implements OnInit {
  @Input() prev: () => void = () => {};
  @Input() next: (
    actionStack: DeviceActionStackService,
    poolAdditionalData: PoolAdditionalData,
  ) => void = () => {};

  @Input() productClass: IProduct | null = null;

  @Input() pool: Pool | null = null;

  @Input() poolAdditionalData: PoolAdditionalData | null = null;

  @Input() actionStackParam: any | null = null;
  actionStack: DeviceActionStackService | null = null;

  public currentForm: FormGroup | null = null;

  private modalController = inject(ModalController);
  private slideModalController = inject(SlideModalController);
  private fb = inject(FormBuilder);
  private edroNetworkManager = inject(EdroNetworkManager);

  private defaultData: any = null;

  constructor() {
    this.P0003606EnSavoirPlusDosage =
      this.P0003606EnSavoirPlusDosage.bind(this);
  }

  ngOnInit() {
    this.actionStack = new DeviceActionStackService(
      this.productClass!,
      this.edroNetworkManager,
      this.actionStackParam,
    );

    this.initForm();
  }

  private initForm() {
    this.defaultData = JSON.stringify({
      volume: this.poolAdditionalData?.cubageBassin ?? '',
      debit: this.poolAdditionalData?.debitPompeDoseusePh ?? 2.4,
      position: this.poolAdditionalData?.positionInjectionCorrecteurPH ?? 0,
    });

    this.currentForm = this.fb.group({
      volume: [
        this.poolAdditionalData?.cubageBassin ?? '',
        [
          Validators.required, // Champ requis
          Validators.pattern('^[0-9]+$'), // Entiers uniquement
        ],
      ],
      debit: [
        this.poolAdditionalData?.debitPompeDoseusePh ?? 2.4,
        [Validators.required, Validators.pattern('^[0-9]+([.][0-9]+)?$')],
      ],
      position: [
        this.poolAdditionalData?.positionInjectionCorrecteurPH ?? 0,
        [],
      ],
    });
  }

  // Méthode pour ajuster la valeur du volume au blur
  onVolumeBlur(event: any) {
    let value = parseInt(event.target.value);

    if (isNaN(value) || value < 5) {
      value = 5;
    } else if (value > 200) {
      value = 200;
    }

    this.currentForm!.controls['volume'].setValue(value);
  }

  // Méthode pour ajuster la valeur du débit au blur
  onDebitBlur(event: any) {
    let valueStr = event.target.value.replace(',', '.'); // Normalise la virgule
    let value = parseFloat(valueStr);

    if (isNaN(value) || value < 0.5) {
      value = 0.5;
    } else if (value > 5.0) {
      value = 5.0;
    }

    // Formate avec 1 décimale si nécessaire
    const formattedValue =
      value % 1 === 0 ? value.toString() : value.toFixed(1);
    this.currentForm!.controls['debit'].setValue(formattedValue);
  }

  async P0003606EnSavoirPlusDosage() {
    const modal = await this.slideModalController.create({
      component: FunctionEnSavoirPlusModalComponent,
      componentProps: {
        description: 'P0003606.installation.esp',
      },
      config: {
        position: 'right',
        size: 'medium',
        showBackdrop: true,
        backdropDismiss: true,
      },
      animationConfig: {
        duration: 300,
        easing: 'ease-out',
      },
    });

    await modal.present();
  }

  async openModal() {
    const modal = await this.modalController.create({
      component: TwoChoicesModalComponent,
      componentProps: {
        title: 'P0003606.modal.positiontype.title',
        value: this.currentForm!.controls['position'].value,
        firstChoice: {
          title: 'P0003606.modal.positiontype.option0',
          image:
            '/assets/ecosystem/images/products/P0003606/P0003605-position-0-black.png',
          value: 0,
        },
        secondChoice: {
          title: 'P0003606.modal.positiontype.option1',
          image:
            '/assets/ecosystem/images/products/P0003606/P0003605-position-1-black.png',
          value: 1,
        },
      },
      cssClass: 'dynamic-height',
    });

    await modal.present();

    const { data } = await modal.onDidDismiss();
    if (data) {
      this.currentForm!.controls['position'].setValue(data.value);
    }
  }

  onSubmit() {
    var data = {
      volume: this.currentForm!.controls['volume']?.value,
      debit: this.currentForm!.controls['debit']?.value,
      position: this.currentForm!.controls['position']?.value,
    };

    if (JSON.stringify(data) !== this.defaultData) {
      this.poolAdditionalData!.cubageBassin = parseInt(data.volume);
      this.poolAdditionalData!.debitPompeDoseusePh = parseFloat(data.debit);
      this.poolAdditionalData!.positionInjectionCorrecteurPH = data.position;
    }

    this.next(this.actionStack!, this.poolAdditionalData!);
  }

  protected readonly P0003606PositionTypeEnum = P0003606PositionTypeEnum;
}
